import { lazy, Suspense, useMemo, useState, type ReactNode } from 'react';
import {
  signerForRecordingConsent,
  useRecordingConsentStatus,
} from '../services/use-recording-consent-status';
import type { RecordingConsentPort } from './recording-consent-types';

// PROD-3195. "Sign consent now" opens the fill-and-sign popup (PDF rendering), so it loads only when asked.
const RecordingConsentSignFlow = lazy(() =>
  import('./RecordingConsentSignFlow').then((m) => ({ default: m.RecordingConsentSignFlow })),
);

/**
 * PROD-3195. Owns the patient's recording-consent check for the ambient panel: runs it in the
 * background when the encounter opens, hands the panel its port, and mounts the sign flow on request.
 *
 * A component of its own, mounted only where the panel is, so the page itself never asks for a query
 * client for this check (the encounter page's test harnesses mock each service hook and render with
 * none), and the check only runs where there is a panel to gate.
 */
export function RecordingConsentHost({
  patientId,
  encounterId,
  children,
}: {
  patientId: string | null | undefined;
  encounterId: string | null | undefined;
  children: (port: RecordingConsentPort) => ReactNode;
}) {
  const check = useRecordingConsentStatus(encounterId);
  const [signOpen, setSignOpen] = useState(false);
  const port = useMemo<RecordingConsentPort>(
    () => ({ status: check.status, onSignNow: () => setSignOpen(true) }),
    [check.status],
  );
  return (
    <>
      {children(port)}
      {signOpen && patientId && encounterId ? (
        // A minor's consent needs the guardian on file, an adult's the patient; closing or signing
        // unmounts it, and a signature re-checks the status (no reload).
        <Suspense fallback={null}>
          <RecordingConsentSignFlow
            patientId={patientId}
            encounterId={encounterId}
            signer={signerForRecordingConsent(check)}
            onClose={() => setSignOpen(false)}
          />
        </Suspense>
      ) : null}
    </>
  );
}
